import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import { supabase } from '../supabase/supabase';

const INVALID_SESSION_MESSAGE =
  'Invitasjons- eller tilbakestillingslenken er utløpt eller ugyldig. Be om ny invitasjon.';

const getAuthTokensFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash || '';
  const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;
  const hashParts = hashWithoutPrefix.split('#');
  const routePart = hashParts.shift() || '';
  const routeQueryIndex = routePart.indexOf('?');
  const routeQuery = routeQueryIndex >= 0 ? routePart.slice(routeQueryIndex + 1) : '';
  const nestedHash = hashParts.join('#');

  const routeParams = new URLSearchParams(routeQuery);
  const nestedHashParams = new URLSearchParams(nestedHash);

  const getParam = name =>
    searchParams.get(name) ||
    routeParams.get(name) ||
    nestedHashParams.get(name);

  const accessToken = getParam('access_token');
  const refreshToken = getParam('refresh_token');

  if (!accessToken || !refreshToken) {
    return null;
  }

  return {
    access_token: accessToken,
    refresh_token: refreshToken
  };
};

const getRecoveryTokenFromUrl = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  const searchParams = new URLSearchParams(window.location.search);
  const hash = window.location.hash || '';
  const hashWithoutPrefix = hash.startsWith('#') ? hash.slice(1) : hash;
  const hashParts = hashWithoutPrefix.split('#');
  const routePart = hashParts.shift() || '';
  const routeQueryIndex = routePart.indexOf('?');
  const routeQuery = routeQueryIndex >= 0 ? routePart.slice(routeQueryIndex + 1) : '';
  const nestedHash = hashParts.join('#');

  const routeParams = new URLSearchParams(routeQuery);
  const nestedHashParams = new URLSearchParams(nestedHash);

  const getParam = name =>
    searchParams.get(name) ||
    routeParams.get(name) ||
    nestedHashParams.get(name);

  const tokenHash = getParam('token_hash');
  const type = getParam('type');

  if (!tokenHash || type !== 'recovery') {
    return null;
  }

  return {
    token_hash: tokenHash,
    type
  };
};

const ResetPassword = () => {
  const navigate = useNavigate();
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState('');
  const [loadingSession, setLoadingSession] = useState(true);

  useEffect(() => {
    let mounted = true;

    const handleRecovery = (event, session) => {
      if (event === 'PASSWORD_RECOVERY' || event === 'SIGNED_IN') {
        if (session) {
          setSessionReady(true);
          setSessionError('');
          setLoadingSession(false);
          return;
        }

        setSessionReady(false);
        setSessionError(INVALID_SESSION_MESSAGE);
        setLoadingSession(false);
      }
    };

    const establishSession = async () => {
      try {
        const authTokens = getAuthTokensFromUrl();
        const recoveryToken = getRecoveryTokenFromUrl();

        if (authTokens) {
          const { error: tokenError } = await supabase.auth.setSession(authTokens);

          if (tokenError) {
            throw tokenError;
          }
        } else if (recoveryToken) {
          const { error: recoveryError } = await supabase.auth.verifyOtp(recoveryToken);

          if (recoveryError) {
            throw recoveryError;
          }
        }

        const {
          data: { session },
          error: sessionError
        } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (!mounted) return;

        if (session) {
          setSessionReady(true);
          setSessionError('');
        } else {
          setSessionReady(false);
          setSessionError(INVALID_SESSION_MESSAGE);
        }
      } catch (sessionException) {
        if (!mounted) return;

        setSessionReady(false);
        setSessionError(sessionException.message || INVALID_SESSION_MESSAGE);
      } finally {
        if (mounted) {
          setLoadingSession(false);
        }
      }
    };

    const { data: { subscription } } = supabase.auth.onAuthStateChange(handleRecovery);

    establishSession();

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (loading) return;

    setError('');
    setStatus('');

    if (newPassword.length < 8) {
      setError('Passordet må være minst 8 tegn.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Passordene er ikke like.');
      return;
    }

    setLoading(true);

    try {
      const {
        data: { session },
        error: sessionError
      } = await supabase.auth.getSession();

      if (sessionError) {
        setError(sessionError.message);
        return;
      }

      if (!session) {
        setSessionReady(false);
        setSessionError(INVALID_SESSION_MESSAGE);
        setError(INVALID_SESSION_MESSAGE);
        return;
      }

      setSessionReady(true);

      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) {
        setError(updateError.message);
        return;
      }

      setStatus('Passordet er oppdatert.');
      setTimeout(() => navigate('/login', { replace: true }), 1500);
    } catch (updateError) {
      setError(updateError.message || 'Kunne ikke oppdatere passordet.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-[2.5rem] shadow-xl border border-slate-200 p-10">
        <div className="text-center mb-10">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200">
            <SafeIcon icon="⚡" className="text-3xl text-white" />
          </div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tight">Sett nytt passord</h1>
          <p className="text-slate-500 font-medium mt-2">Velg et nytt passord for kontoen din.</p>
        </div>

        {(error || sessionError) && (
          <div className="mb-4 p-4 bg-red-50 border border-red-100 text-red-600 text-sm font-bold rounded-xl">
            {error || sessionError}
          </div>
        )}

        {status && (
          <div className="mb-4 p-4 bg-green-50 border border-green-100 text-green-700 text-sm font-bold rounded-xl">
            {status}
          </div>
        )}

        {loadingSession ? (
          <div className="mb-4 p-4 bg-slate-50 border border-slate-200 text-slate-500 text-sm font-bold rounded-xl">
            Kontrollerer invitasjonslenken...
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                Nytt passord
              </label>
              <input
                required
                minLength={8}
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                disabled={loading || !sessionReady}
                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold disabled:opacity-50"
              />
            </div>

            <div>
              <label className="block text-xs font-black text-slate-400 uppercase tracking-widest mb-2">
                Bekreft nytt passord
              </label>
              <input
                required
                minLength={8}
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                disabled={loading || !sessionReady}
                className="w-full px-5 py-4 bg-slate-50 border border-slate-200 rounded-2xl focus:bg-white focus:ring-4 focus:ring-blue-500/10 focus:border-blue-500 outline-none transition-all font-bold disabled:opacity-50"
              />
            </div>

            <button
              type="submit"
              disabled={loading || !sessionReady}
              className="w-full py-5 bg-slate-900 text-white rounded-2xl font-black uppercase tracking-[0.2em] hover:bg-slate-800 transition-all shadow-xl disabled:opacity-50 active:scale-[0.98]"
            >
              {loading ? 'Lagrer...' : 'Lagre nytt passord'}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;